/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import * as React from "react";

import styled from "styled-components";

import { YouTubePlayButton } from "src/components/Block/BlockTypes/LinkBlock/Youtube/Icons";

export type imgResolution = "default" | "mqdefault" | "hqdefault" | "sddefault" | "maxresdefault";

interface YoutubeFacadeProps {
  id: string; // id of the video or playlist
  title: string;
  activatedClass?: string; // Pass the string class for the active state
  adNetwork?: boolean; // Default true, to preconnect or not to doubleclick addresses called by YouTube iframe (the adnetwork from Google)
  aspectHeight?: number;
  aspectWidth?: number;
  iframeClass?: string;
  noCookie?: boolean;
  cookie?: boolean;
  params?: string; // any params you want to pass to the URL, assume we already had '&' and pass your parameters string
  playerClass?: string;
  disableMouse: boolean;
  playlist?: boolean; // Use true when your ID be from a playlist
  playlistCoverId?: string; // The ids for playlists did not bring the cover in a pattern to render so you'll need pick up a video from the playlist (or in fact, whatever id) and use to render the cover.
  poster?: imgResolution;
  webp?: boolean;
  wrapperClass?: string;
  onIframeAdded?: () => void;
  muted?: boolean;
  thumbnail?: string;
  rel?: string;
}

/**
 * From https://github.com/ibrahimcesar/react-lite-youtube-embed
 */
export function YoutubeFacade(props: YoutubeFacadeProps) {
  const [preconnected, setPreconnected] = React.useState(false);
  const [iframe, setIframe] = React.useState(false);
  const videoId = encodeURIComponent(props.id);

  const videoPlaylisCovertId =
    typeof props.playlistCoverId === "string" ? encodeURIComponent(props.playlistCoverId) : null;
  const videoTitle = props.title;
  const posterImp = props.poster || "hqdefault";
  const paramsImp = `&${props.params}` || "";
  const mutedImp = props.muted ? "&mute=1" : "";
  const format = props.webp ? "webp" : "jpg";
  const vi = props.webp ? "vi_webp" : "vi";
  const posterUrl =
    props.thumbnail ||
    (!props.playlist
      ? `https://i.ytimg.com/${vi}/${videoId}/${posterImp}.${format}`
      : `https://i.ytimg.com/${vi}/${videoPlaylisCovertId}/${posterImp}.${format}`);

  let youtubeURL = props.noCookie ? "https://www.youtube-nocookie.com" : "https://www.youtube.com";
  youtubeURL = props.cookie ? "https://www.youtube.com" : "https://www.youtube-nocookie.com";

  const iframeSrc = !props.playlist
    ? `${youtubeURL}/embed/${videoId}?autoplay=1&state=1${mutedImp}${paramsImp}`
    : `${youtubeURL}/embed/videoseries?autoplay=1${mutedImp}&list=${videoId}${paramsImp}`;

  const activatedClassImp = props.activatedClass || "lyt-activated";
  const adNetworkImp = props.adNetwork || false;
  const aspectHeight = props.aspectHeight || 9;
  const aspectWidth = props.aspectWidth || 16;

  const iframeClassImp = props.iframeClass || "";
  const wrapperClassImp = props.wrapperClass || "yt-lite";

  const onIframeAdded = props.onIframeAdded || function () {};
  const rel = props.rel ? "prefetch" : "preload";

  const warmConnections = () => {
    if (preconnected) return;
    setPreconnected(true);
  };

  const addIframe = () => {
    if (iframe) return;
    setIframe(true);
  };

  React.useEffect(() => {
    if (iframe) {
      onIframeAdded();
    }
  }, [iframe]);

  return (
    <>
      <link rel={rel} href={posterUrl} as="image" />
      <>
        {preconnected && (
          <>
            <link rel="preconnect" href={youtubeURL} />
            <link rel="preconnect" href="https://www.google.com" />
            {adNetworkImp && (
              <>
                <link rel="preconnect" href="https://static.doubleclick.net" />
                <link rel="preconnect" href="https://googleads.g.doubleclick.net" />
              </>
            )}
          </>
        )}
      </>
      <article
        onPointerOver={warmConnections}
        onClick={addIframe}
        className={`${wrapperClassImp} ${iframe ? activatedClassImp : ""}`}
        data-title={videoTitle}
        style={{
          backgroundImage: `url(${posterUrl})`,
          ...({
            "--aspect-ratio": `${(aspectHeight / aspectWidth) * 100}%`,
          } as React.CSSProperties),
        }}
      >
        <YoutubeTopbar>
          <ChannelIconWrapper>
            {/* <YoutubeImage
              src={`https://${process.env.ENVIRONMENT}-vht-file-preview-bucket.s3.amazonaws.com/youtube-channel-${id}.jpeg?dummy=${dummy}`}
            /> */}
          </ChannelIconWrapper>
          <YoutubeTitle>{videoTitle}</YoutubeTitle>
        </YoutubeTopbar>

        <YoutubePlayWrapper>
          <YouTubePlayButton />
        </YoutubePlayWrapper>
        {iframe && (
          <iframe
            className={iframeClassImp}
            title={videoTitle}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              pointerEvents: props.disableMouse ? "none" : "auto",
            }}
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            src={iframeSrc}
          ></iframe>
        )}
      </article>
    </>
  );
}

const YoutubePlayWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 80px;
  align-self: center;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  flex-direction: column;
`;

const ChannelIconWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-top: 12px;
  margin-left: 12px;
  overflow: hidden;
`;

// const ButtonLabel = styled.div`
//   position: relative;
//   font-size: 14px;
//   font-family: RobotoLight, sans-serif;
//   color: #fff;
//   white-space: nowrap;
// `;

// const IconWrapper = styled.div`
//   position: relative;
//   height: 35px;
//   width: 35x;
// `;

// const ButtonWrapper = styled.div`
//   position: relative;
//   display: flex;
//   flex-direction: column;
//   align-items: center;
//   line-height: 20px;
//   padding-top: 6px;
//   margin-right: 10px;
//   width: 70px;
//   flex-shrink: 0;
// `;

const YoutubeTopbar = styled.div`
  position: absolute;
  display: flex;
  width: 100%;
  height: 60px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0));
`;

const YoutubeTitle = styled.div`
  position: relative;
  flex-grow: 1;
  font-size: 18px;
  margin-top: 21px;
  margin-left: 10px;
  margin-right: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 200;
  font-family: "RobotoLight", Arial, sans-serif;
  letter-spacing: 0.12px;
  color: white;
  text-shadow: 0 0 2px rgb(0 0 0 / 50%);
`;
